import {
  Camera,
  Clock,
  FileText,
  Globe,
  Loader,
  Puzzle,
  Type,
  X,
} from "lucide-react"
import styles from "./styles.module.scss"
import {
  useAutoTranslation,
  useContentCache,
  useGetLibraryTempContent,
} from "@/entities"
import {
  contentControllerCancelTask,
  ContentItemCreationMode,
  type RemoveTaskDto,
  type TempContentItem,
} from "@/shared/api/generated"
import { useMutation } from "@tanstack/react-query"
import { DISCOVERED_TRANSLATION_KEYS, LANG_KEYS } from "@/shared/constants"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import moment from "moment"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

const formatDate = (date: string) => {
  return moment(date).format("D MMM, h:mm A")
}

const ICON_SIZE = 20

const ICONS: Record<ContentItemCreationMode, ReactNode> = {
  [ContentItemCreationMode.FILE]: <FileText size={ICON_SIZE} />,
  [ContentItemCreationMode.LINK]: <Globe size={ICON_SIZE} />,
  [ContentItemCreationMode.TEXT]: <Type size={ICON_SIZE} />,
  [ContentItemCreationMode.CAMERA]: <Camera size={ICON_SIZE} />,
  [ContentItemCreationMode.SELECTION]: <Puzzle size={ICON_SIZE} />,
}

const TempContentProcessInfo = ({
  processInfo,
  creationMode,
}: {
  processInfo: string
  creationMode: ContentItemCreationMode
}) => {
  const [title, url] = processInfo.split(": ")
  if (creationMode === ContentItemCreationMode.LINK) {
    return (
      <div>
        <p>{title}</p>
        <a
          href={url}
          target="_blank"
          className="underline!"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      </div>
    )
  }

  return <p>{processInfo}</p>
}

export const TempContentEl = ({ item }: { item: TempContentItem }) => {
  const { t } = useAutoTranslation()

  const contentCache = useContentCache()

  const cancelMutation = useMutation({
    mutationFn: (dto: RemoveTaskDto) => contentControllerCancelTask(dto),
    async onSuccess() {
      contentCache.invalidateTempContent()
      toast.success(t(LANG_KEYS.PROCESSING_CANCELLED))
    },
  })

  const handleCancel = async () => {
    const isSure = confirm(t(LANG_KEYS.CANCEL_PROCESSING_DESCRIPTION))
    if (!isSure) return
    await cancelMutation.mutateAsync({ tempContentId: item.id })
  }

  return (
    <div className={styles.item}>
      <div className={styles.item__header}>
        <div className="flex items-center gap-2">
          <div className={cn(styles.item__iconWrapper, "bg-transparent!")}>
            <Clock size={ICON_SIZE} />
          </div>
          <h3>
            {t(DISCOVERED_TRANSLATION_KEYS.STARTED_AT)}{" "}
            {formatDate(item.createdAt)}
          </h3>
        </div>

        <Button variant="ghost" size="icon" onClick={handleCancel}>
          <X />
        </Button>
      </div>

      <div className={styles.item__infoWrapper}>
        <div className={styles.item__iconWrapper}>
          {ICONS[item.creationMode as ContentItemCreationMode]}
        </div>

        <TempContentProcessInfo
          processInfo={item.processInfo}
          creationMode={item.creationMode as ContentItemCreationMode}
        />
      </div>
    </div>
  )
}

export const TempContentGrid = () => {
  const { t } = useAutoTranslation()

  const { data, isLoading, isFetching } = useGetLibraryTempContent()

  if (isLoading || isFetching || !data || data.length === 0) {
    return null
  }
  return (
    <div>
      <div className={styles.header}>
        <div className={styles.header__title}>
          <Loader />
          <h3>{t(LANG_KEYS.IN_PROGRESS)}</h3>
        </div>
      </div>

      <div className={styles.items}>
        {" "}
        {data.map(item => (
          <TempContentEl key={item.id} item={item} />
        ))}
      </div>
    </div>
  )
}
